import React, { useEffect, useRef, useState } from 'react'
import PageTitle from '../../pageTitle.component'
import { IArticleHead } from '../../../interfaces/article'
import Marker from './marker.component'
import FeaturedNewsThumbnail from './featuredNewsThumbnail.component'
import LightBackground from '../lightBackground.component'
import useTimer from '../../../hooks/useTimer'
import { useIntersection } from '../../../hooks/useIntersection'
import { useAppContext } from '../../../context/siteMapContext'

interface IFeaturedNewsPanelProps {
  articles: IArticleHead[]
}

const FeaturedNewsPanel: React.FC<IFeaturedNewsPanelProps> = ({ articles }) => {
  const [activeItemId, setActiveItemId] = useState('')
  const [stopCarousel, setStopCarousel] = useState(false);
  const ref = useRef(null);
  const inViewport = useIntersection(ref, '0px');
  const { highContrastOn } = useAppContext();

  useEffect(() => {
    if (articles.length) {
      setActiveItemId(articles[0].slug)
    }
  }, [articles])

  const handleClick = (id: string): void => {
    setActiveItemId(id)
  }

  useTimer(
    activeItemId,
    articles.map(({ slug }) => slug),
    setActiveItemId,
    stopCarousel
  )

  useEffect(() => {
    const keyDownHandler = (event: any) => {
      if (event.key === " " && inViewport) {
        event.preventDefault();
        setStopCarousel(!stopCarousel);
      }
    };

    document.addEventListener("keydown", keyDownHandler);
    return () => {
      document.removeEventListener("keydown", keyDownHandler);
    };
  }, [stopCarousel, inViewport]);

  const [touchStart, setTouchStart] = useState(null)
  const [touchEnd, setTouchEnd] = useState(null)

  const minSwipeDistance = 50

  const onTouchStart = (e: any) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = (e: any) => setTouchEnd(e.targetTouches[0].clientX)

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return
    const distance = touchStart - touchEnd;

    const active = articles.findIndex(article => article.slug === activeItemId);

    if (distance < -minSwipeDistance) {
      setStopCarousel(true);
      setActiveItemId(articles.map(({ slug }) => slug)[(active - 1 + articles.length) % articles.length])
      setStopCarousel(false);
    } else if (distance > minSwipeDistance) {
      setStopCarousel(true);
      setActiveItemId(articles.map(({ slug }) => slug)[(active + 1) % articles.length])
      setStopCarousel(false);
    }
  }

  const prev = (e: Event) => {
    const active = articles.findIndex(article => article.slug === activeItemId);
    setActiveItemId(articles.map(({ slug }) => slug)[(active - 1 + articles.length) % articles.length])
    e.preventDefault();
    return false;

  }

  const next = (e: Event) => {
    const active = articles.findIndex(article => article.slug === activeItemId);
    setActiveItemId(articles.map(({ slug }) => slug)[(active + 1) % articles.length])
    e.preventDefault();
    return false;
  }

  return (
    <section className=' relative pt-16 pb-0 2xl:gap-5.5 mx-page-side-negative-margin mt-[72px] 2xl:mt-[-48px] xl:mb-[72px]' ref={ref}>
      <div className='w-full h-full flex justify-center px-page-side-padding pb-[60px] xl:pb-[0px] '>
        <div className={`z-1 w-full py-12 flex xl:w-8/12 justify-between xl:justify-center flex-col xl:flex-row ${highContrastOn ? '' : 'xl:bg-gray-color'}`}>
          <div className='z-1 w-full xl:w-[22%] flex flex-col gap-8 pb-8'>
            <div className='flex flex-col justify-between h-full'>
              <div className='pl-6 2xl:pl-16'>
                <PageTitle title='Friss híreink' />
              </div>
            </div>
          </div>
          <div className='z-1 w-full xl:w-[56%] h-full'>
            <div className='relative xl:pb-0 h-full w-full'
              onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
            >
              {articles.map((item) => {
                return (
                  <div
                    key={item.slug}
                  >
                    <FeaturedNewsThumbnail
                      articleHead={item}
                      activeItemId={activeItemId}
                      prev={prev}
                      next={next}
                    />
                  </div>
                )
              })}
            </div>
          </div>
          <div className='hidden z-[-1] w-full xl:w-[22%] xl:flex flex-col gap-8 pb-8 justify-center align-middle select-none text-[70px] cursor-pointer text-white text-center' ></div>
          <div className='flex xl:hidden justify-center items-center gap-2 xl:gap-6 xl:justify-start mt-[16px]'>
            {articles.map(({ slug }) => (
              <Marker
                id={slug}
                activeItemId={activeItemId}
                handleClick={handleClick}
                key={slug}
              />
            ))}
          </div>
        </div>
      </div>
      <div className='block xl:hidden'>
        <LightBackground />
      </div>
    </section>
  )
}

export default FeaturedNewsPanel
